// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect, useMemo, useRef } from 'react';

import { type MachineProfile, useEvolvePaginatedGet } from '@evolvemep/api-ts';
import { Box, Collapse, Stack, Text, UnstyledButton } from '@mantine/core';
import { useDisclosure, useElementSize, useMergedRef } from '@mantine/hooks';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { TriggerOnVisible } from 'hooks/useOnScreen';
import { useBendCreatorFormContext } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/useBendCreatorFormContext';
import { useBendCreatorValidationContext } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/useBendCreatorValidationContext';
import classes from 'modules/Shop/EvolveFabrication/BendCreator/Nav/steps/steps.module.css';
import { formatConduit } from 'modules/Shop/EvolveFabrication/BendCreator/Nav/stepValues';
import { useConduits } from 'modules/Shop/EvolveFabrication/useConduits';

type BenderRowProps = {
  profile: MachineProfile;
  selected: boolean;
  /** When set, the bender cannot take the chosen conduit; the row is greyed out and shows this reason. */
  unavailableReason?: string;
  onSelect: (profile: MachineProfile) => void;
};

const BenderRow = ({ profile, selected, unavailableReason, onSelect }: BenderRowProps) => (
  <UnstyledButton
    className={classes.row}
    role="radio"
    aria-checked={selected}
    data-active={selected || undefined}
    disabled={isNotNil(unavailableReason)}
    onClick={() => onSelect(profile)}
  >
    <Box className={classes.rowName}>
      {profile.name}
      <Text component="span" display="block" className={classes.rowDetail}>
        {unavailableReason ?? profile.machineName}
      </Text>
    </Box>
  </UnstyledButton>
);

type BenderListProps = {
  onBenderSelected: () => void;
};

/** The bender profiles that can take the chosen conduit, followed by the ones that can't, greyed out. */
const BenderList = ({ onBenderSelected }: BenderListProps) => {
  const form = useBendCreatorFormContext();
  const { scheduleValidation } = useBendCreatorValidationContext();
  const { conduitTypes, conduitSizes } = useConduits();
  const { conduitTypeId, conduitSizeId, benderProfile } = form.values;
  const materialChosen = isNotNil(conduitTypeId) && isNotNil(conduitSizeId);
  const conduitLabel = formatConduit(form.values, { conduitTypes, conduitSizes });

  const {
    data: allProfiles,
    fetchNextPage: fetchNextProfiles,
    loading: loadingAllProfiles,
  } = useEvolvePaginatedGet('shop/fabrication/machineProfile');

  const compatibleOpts = useMemo(
    () => ({
      perPage: 50,
      defaultConfig: {
        params: { conduitTypeId: conduitTypeId ?? undefined, conduitSizeId: conduitSizeId ?? undefined },
      },
    }),
    [conduitTypeId, conduitSizeId],
  );
  const {
    data: compatibleProfiles,
    fetchNextPage: fetchNextCompatibleProfiles,
    loading: loadingCompatibleProfiles,
    entireCount: compatibleCount,
    setDefaultOpts: setCompatibleOpts,
  } = useEvolvePaginatedGet('shop/fabrication/machineProfile', compatibleOpts);
  useEffect(() => {
    setCompatibleOpts(compatibleOpts);
  }, [compatibleOpts, setCompatibleOpts]);

  const compatibleIds = useMemo(
    () => new Set(compatibleProfiles.map(({ machineProfileId }) => machineProfileId)),
    [compatibleProfiles],
  );
  // Only list the benders that can't take the material once every compatible one is known, so none shows as greyed out
  // just because its compatible page hasn't loaded yet.
  const allCompatibleLoaded = isNotNil(compatibleCount) && compatibleProfiles.length >= compatibleCount;
  const incompatibleProfiles = allProfiles.filter(({ machineProfileId }) => !compatibleIds.has(machineProfileId));

  const selectProfile = (profile: MachineProfile) => {
    form.setFieldValue('benderProfile', profile);
    scheduleValidation('benderProfile');
    onBenderSelected();
  };
  const isSelected = (profile: MachineProfile) => profile.machineProfileId === benderProfile?.machineProfileId;

  if (!materialChosen) {
    return (
      <Box className={classes.rows} role="radiogroup" aria-label="Bender">
        {allProfiles.map((profile) => (
          <BenderRow
            key={profile.machineProfileId}
            profile={profile}
            selected={isSelected(profile)}
            onSelect={selectProfile}
          />
        ))}
        <TriggerOnVisible onVisible={fetchNextProfiles} loading={loadingAllProfiles} />
      </Box>
    );
  }

  return (
    <Stack gap={20}>
      <Box className={classes.rows} role="radiogroup" aria-label="Bender">
        {compatibleProfiles.map((profile) => (
          <BenderRow
            key={profile.machineProfileId}
            profile={profile}
            selected={isSelected(profile)}
            onSelect={selectProfile}
          />
        ))}
        <TriggerOnVisible onVisible={fetchNextCompatibleProfiles} loading={loadingCompatibleProfiles} />
        {allCompatibleLoaded && compatibleProfiles.length === 0 && (
          <Text c="dimmed" fz="sm">
            No bender can take {conduitLabel}.
          </Text>
        )}
      </Box>
      {allCompatibleLoaded && (
        <Stack gap={8}>
          {incompatibleProfiles.length > 0 && <Text className={classes.sectionLabel}>Cannot take {conduitLabel}</Text>}
          <Box className={classes.rows}>
            {incompatibleProfiles.map((profile) => (
              <BenderRow
                key={profile.machineProfileId}
                profile={profile}
                selected={false}
                unavailableReason={isNil(conduitLabel) ? profile.machineName : `Not for ${conduitLabel}`}
                onSelect={selectProfile}
              />
            ))}
            <TriggerOnVisible onVisible={fetchNextProfiles} loading={loadingAllProfiles} />
          </Box>
        </Stack>
      )}
    </Stack>
  );
};

/**
 * The bender, kept low-key at the bottom of the Dimensions step: one line with the chosen bender, which expands to the
 * list of benders inside the same box. The bender is picked automatically once the material is chosen; see `useKeepCompatibleBender`.
 */
export const BenderPicker = () => {
  const { benderProfile } = useBendCreatorFormContext().values;
  const [expanded, { toggle, close }] = useDisclosure(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const { ref: boxSizeRef, height: boxHeight } = useElementSize<HTMLDivElement>();
  const mergedBoxRef = useMergedRef(boxRef, boxSizeRef);

  // The box sits at the bottom of the step, so keep it in view while it opens and while its benders load in.
  useEffect(() => {
    if (expanded) boxRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [expanded, /* effect dep */ boxHeight]);

  return (
    <Box ref={mergedBoxRef} className={classes.benderBox}>
      <UnstyledButton className={classes.disclosure} aria-expanded={expanded} onClick={toggle}>
        <Box className={classes.disclosureText}>
          <Text component="span" className={classes.disclosureLabel}>
            Validation based on this bender:
          </Text>
          <Text component="span" className={classes.disclosureValue}>
            {benderProfile?.name ?? 'Not selected'}
          </Text>
        </Box>
        <EvolveIcon icon="ArrowRight" size="xs" className={classes.disclosureChevron} />
      </UnstyledButton>
      <Collapse expanded={expanded}>
        <Box className={classes.benderOptions}>
          <BenderList onBenderSelected={close} />
        </Box>
      </Collapse>
    </Box>
  );
};
